import { ApplicationRef, DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { interval, merge, timer } from 'rxjs';
import { filter, first, take } from 'rxjs/operators';

/**
 * Detects new deployments via the Angular service worker and applies them
 * seamlessly on the user's next route navigation — so the app picks up new
 * changes without the user having to hard-refresh, and without interrupting
 * them mid-screen.
 *
 * Relies on the server serving `ngsw-worker.js` / `ngsw.json` / `index.html`
 * with `Cache-Control: no-cache`, otherwise the browser HTTP cache can hide new
 * versions from the service worker. See nginx/default.conf.
 *
 * Ported from Gritstone.Talos.ImageProcessingUINew. Differences here: no
 * Application Insights in this project, so the update lifecycle is logged to the
 * console instead; and `enabled` is derived from `isDevMode()` rather than an
 * environment file (this project has none).
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** How often to poll for a new version. */
  private static readonly CHECK_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

  /** Max wait for the app to become "stable" before doing the first check anyway. */
  private static readonly INITIAL_CHECK_FALLBACK_MS = 20 * 1000; // 20 seconds

  /** sessionStorage key guarding against repeated unrecoverable-state reloads. */
  private static readonly UNRECOVERABLE_RELOAD_GUARD_KEY = 'sw-unrecoverable-reload';

  /** True once a new version has been downloaded and is ready to apply. */
  private updatePending = false;

  /** Guards against kicking off the reload more than once. */
  private applyingUpdate = false;

  init(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.listenForVersionUpdates();
    this.applyUpdateOnNextNavigation();
    this.listenForUnrecoverableState();
    this.schedulePeriodicChecks();
  }

  private listenForVersionUpdates(): void {
    this.swUpdate.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      switch (event.type) {
        case 'VERSION_DETECTED':
          console.info('[pwa-update] new version detected', event.version.hash);
          break;

        case 'VERSION_READY':
          // Mark the new version ready, but do NOT activate it yet. Activating
          // here would make the service worker stop serving the *current* (old)
          // version's lazy chunks to this still-running tab; any not-yet-loaded
          // route would then 404 on its old hashed chunk and force an abrupt
          // reload. We instead activate at the moment we reload (see
          // applyUpdateOnNextNavigation), so the running app keeps working on
          // the old version until it is torn down.
          this.updatePending = true;
          console.info('[pwa-update] version ready, will apply on next navigation', {
            current: event.currentVersion.hash,
            latest: event.latestVersion.hash
          });
          break;

        case 'VERSION_INSTALLATION_FAILED':
          // A new version failed to download/install — no update will apply
          // until a later check succeeds. Surface it; it is otherwise silent.
          console.error(
            `[pwa-update] version installation failed (${event.version.hash})`,
            event.error
          );
          break;
      }
    });
  }

  private applyUpdateOnNextNavigation(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async (event) => {
        if (this.applyingUpdate) {
          return;
        }

        // If we don't already have a ready update, perform a check now so a
        // navigation picks up a just-deployed version.
        if (!this.updatePending) {
          try {
            await this.swUpdate.checkForUpdate();
          } catch (err) {
            console.error('[pwa-update] failed to check for update during navigation', err);
          }
        }

        if (!this.updatePending) {
          return;
        }

        this.applyingUpdate = true;
        this.updatePending = false;

        // Activate the downloaded version, then replace the in-app navigation
        // with a full page load to the target route so the user lands on their
        // destination running the new version. Reload even if activation fails
        // — the next check cycle will re-detect and retry.
        this.swUpdate
          .activateUpdate()
          .then((activated) => console.info('[pwa-update] applied', { activated }))
          .catch((err) => console.error('[pwa-update] failed to activate update', err))
          .finally(() => globalThis.location.assign(event.url));
      });
  }

  private listenForUnrecoverableState(): void {
    this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      console.error('[pwa-update] service worker is in an unrecoverable state', event.reason);
      // Reload once to recover a fresh worker; a sessionStorage guard prevents
      // an infinite reload loop if the state persists. The guard is cleared on
      // the next successful update check (see schedulePeriodicChecks).
      try {
        if (sessionStorage.getItem(PwaUpdateService.UNRECOVERABLE_RELOAD_GUARD_KEY)) {
          return;
        }
        sessionStorage.setItem(PwaUpdateService.UNRECOVERABLE_RELOAD_GUARD_KEY, '1');
      } catch {
        // sessionStorage unavailable (private mode / blocked) — reload anyway.
      }
      globalThis.location.reload();
    });
  }

  private schedulePeriodicChecks(): void {
    // Run an initial check once the app is stable, but don't wait forever:
    // long-lived async work can keep the app from ever reporting "stable",
    // which would otherwise block every update check.
    const initialCheck$ = merge(
      this.appRef.isStable.pipe(first((stable) => stable === true)),
      timer(PwaUpdateService.INITIAL_CHECK_FALLBACK_MS)
    ).pipe(take(1));

    // The interval runs independently of stability so checks keep happening.
    const periodicChecks$ = interval(PwaUpdateService.CHECK_INTERVAL_MS);

    merge(initialCheck$, periodicChecks$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.swUpdate
          .checkForUpdate()
          .then(() => this.clearUnrecoverableGuard())
          .catch((err) => console.error('[pwa-update] failed to check for update', err));
      });
  }

  /**
   * A successful update check proves the worker is responsive again, so any
   * unrecoverable-state reload guard from a prior load can be released — a
   * future unrecoverable event is then free to reload once more.
   */
  private clearUnrecoverableGuard(): void {
    try {
      sessionStorage.removeItem(PwaUpdateService.UNRECOVERABLE_RELOAD_GUARD_KEY);
    } catch {
      /* sessionStorage unavailable — nothing to clear. */
    }
  }
}
